import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paymentsAPI } from '../api/payments';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const reference = searchParams.get('reference');
      const paypalOrderId = searchParams.get('token');
      const payerId = searchParams.get('PayerID');

      try {
        if (reference) {
          // Paystack callback
          const res = await paymentsAPI.verifyPaystack(reference);
          if (res.success && res.data?.status === 'success') {
            setStatus('success');
          } else {
            setStatus('error');
          }
        } else if (paypalOrderId && payerId) {
          // PayPal callback
          const res = await paymentsAPI.capturePaypal(paypalOrderId);
          if (res.success && res.data?.status === 'COMPLETED') {
            setStatus('success');
          } else {
            setStatus('error');
          }
        } else {
          setStatus('success');
        }
      } catch {
        setStatus('error');
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm text-center"
      >
        <Link
          to="/"
          className="font-serif text-2xl tracking-widest uppercase text-primary block mb-12"
        >
          Lanre Savage
        </Link>

        {status === 'loading' && (
          <div className="space-y-6">
            <LoadingSpinner size="lg" />
            <p className="text-xs tracking-widest uppercase text-nude">
              Confirming your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.6, delay: 0.2 }}
              className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto"
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" className="text-green-600">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </motion.div>
            <h1 className="font-serif text-4xl text-primary">
              Order Confirmed
            </h1>
            <p className="text-xs text-nude tracking-wide leading-relaxed">
              Thank you for your purchase. Your order has been confirmed and
              you will receive an email confirmation shortly.
            </p>
            <div className="space-y-3 pt-4">
              <Link to="/account" className="btn-primary block">
                View My Orders
              </Link>
              <Link
                to="/shop"
                className="btn-secondary block"
              >
                Continue Shopping
              </Link>
            </div>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" className="text-red-500">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h1 className="font-serif text-4xl text-primary">
              Payment Failed
            </h1>
            <p className="text-xs text-nude tracking-wide leading-relaxed">
              There was an issue processing your payment. Please try again
              or contact support.
            </p>
            <div className="space-y-3 pt-4">
              <Link to="/checkout" className="btn-primary block">
                Try Again
              </Link>
              <Link to="/contact" className="btn-secondary block">
                Contact Support
              </Link>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;